<template>
  <Dialog
    :open="show"
    class="eds-modal ev-overflow-auto"
    :class="[{ 'modal-open': show }, dialogClass]"
    style="z-index: 100000"
    :style="dialogStyle"
    :initial-focus="getActiveElement()"
    role="dialog"
    as="section"
  >
    <div class="ev-fixed ev-inset-0 ev-z-10 ev-overflow-y-auto">
      <div class="ev-px-4">
        <DialogOverlay
          class="ev-fixed ev-inset-0"
          :class="dailogOverlayClass"
          :style="dailogOverlayStyle"
          @click="closeModalHandler"
        />
        <span class="ev-inline-block ev-align-middle" aria-hidden="true">
          &#8203;
        </span>
        <div
          class="modal-container"
          :class="containerClass"
          :style="containerStyle"
        >
          <button
            v-if="isClosable"
            class="modal-close eds-link ev-focus:outline-none"
            @click="closeModalHandler"
          >
            <svg
              fill="currentColor"
              width="100%"
              height="100%"
              viewBox="0 0 24 24"
            >
              <path
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
              />
            </svg>
          </button>

          <DialogTitle
            v-if="$slots.header"
            class="modal-header"
            :class="headerClass"
            :style="headerStyle"
            as="header"
          >
            <div class="modal-title">
              <slot name="header" />
            </div>
          </DialogTitle>
          <div
            class="modal-content"
            :class="bodyClass"
            :style="bodyStyle"
            as="div"
          >
            <slot name="body" />
          </div>

          <footer
            v-if="$slots.footer"
            class="modal-footer"
            :class="footerClass"
            :style="footerStyle"
          >
            <slot name="footer" :closeModal="closeModalHandler">
              <button
                class="eds-button button-basic"
                @click="closeModalHandler"
              >
                Dismiss
              </button>
            </slot>
          </footer>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script lang="ts">
  import { defineComponent, ref } from 'vue';
  import { Dialog, DialogOverlay, DialogTitle } from '@headlessui/vue';

  export default defineComponent({
    name: 'Modal',
    emits: ['onModalClose'],
    props: {
      isClosable: {
        type: Boolean,
        required: false,
        default: true,
      },
      show: {
        type: Boolean,
        required: false,
        default: true,
      },
      color: {
        type: String,
        required: false,
        default: 'primary',
        validator: (value: string) =>
          ['primary', 'basic', 'tertiary', 'danger'].includes(value),
      },
      containerClass: {
        type: String,
        required: false,
        default: '',
      },
      containerStyle: {
        type: Object,
        required: false,
      },
      headerClass: {
        type: String,
        required: false,
        default: '',
      },
      headerStyle: {
        type: Object,
        required: false,
      },
      bodyClass: {
        type: String,
        required: false,
        default: '',
      },
      bodyStyle: {
        type: Object,
        required: false,
      },
      footerClass: {
        type: String,
        required: false,
        default: '',
      },
      footerStyle: {
        type: Object,
        required: false,
      },
      dailogOverlayClass: {
        type: String,
        required: false,
        default: '',
      },
      dailogOverlayStyle: {
        type: Object,
        required: false,
      },
      dialogClass: {
        type: String,
        required: false,
        default: '',
      },
      dialogStyle: {
        type: Object,
        required: false,
      },
    },
    components: {
      Dialog,
      DialogOverlay,
      DialogTitle,
    },
    setup: (props, { emit }) => {
      let focusElementRef = ref(null);

      const closeModalHandler = (): void => {
        if (props.isClosable) {
          emit('onModalClose');
        }
      };

      /** todo: remove using getActiveElement
       * https://github.com/tailwindlabs/headlessui/issues/825
       * tempory fix to return active element
       * */
      const getActiveElement = () => {
        return document.activeElement as HTMLElement;
      };

      return {
        focusElementRef,
        closeModalHandler,
        getActiveElement,
      };
    },
  });
</script>
